import { useEffect, useState, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { callApi } from "../api/api";

/* 🔹 SIMPLE IN-MEMORY CACHE */
let cachedPermissions = null;

export default function useRBAC() {
  const { instance } = useMsal();

  const [permissions, setPermissions] = useState(cachedPermissions || []);
  const [loading, setLoading] = useState(!cachedPermissions);

  /* ================= LOAD PERMISSIONS ================= */
  const loadPermissions = useCallback(async () => {
    try {
      const account = instance.getActiveAccount();

      if (!account) {
        setPermissions([]);
        setLoading(false);
        return;
      }

      /* ✅ USE CACHE */
      if (cachedPermissions) {
        setPermissions(cachedPermissions);
        setLoading(false);
        return;
      }

      /* 🔥 UPDATED API */
      const res = await callApi(instance, [], "GET", "/api/user/userpermissions");

      const perms = res?.data?.data || [];

      cachedPermissions = perms;
      setPermissions(perms);
    } catch (err) {
      console.error("RBAC load failed", err);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [instance]);

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    const account = instance.getActiveAccount();
    if (account) {
      loadPermissions();
    }
  }, [instance, loadPermissions]);

  /* ================= ADMIN CHECK ================= */
  const isAdmin = useMemo(() => {
    return permissions?.some(
      (p) => p.resource_code === "ADMIN"
    );
  }, [permissions]);

  /* ================= ACCESS CHECK ================= */
  const hasAccess = useCallback(
    (resource, access) => {
      if (!permissions?.length) return false;

      /* 🔥 ADMIN OVERRIDE */
      if (isAdmin) return true;

      return permissions.some(
        (p) =>
          p.resource_code === resource &&
          (p.access_level_code === access ||
            p.access_level_code === "WRITE")
      );
    },
    [permissions, isAdmin]
  );

  /* ================= RETURN ================= */
  return {
    permissions,
    hasAccess,
    isAdmin,   // 🔥 useful for sidebar/dashboard
    loading,
  };
}